import {
  webhookRegenerateTokenUrl,
  webhookRunDetailUrl,
  webhookRunsUrl,
  WEBHOOKS_BULK_DELETE_URL,
  WEBHOOKS_BULK_TOGGLE_URL,
  WEBHOOKS_URL,
  webhookToggleUrl,
  webhookUrl,
} from "../constants/automation-api-urls";
import type {
  IAgentRunDetail,
  IBulkOperationResult,
  ICreateWebhookPayload,
  IWebhook,
  IWebhookRun,
  IWebhookRunsListResponse,
  TBulkDeleteWebhooksPayload,
  TBulkToggleWebhooksPayload,
} from "../types";
import type {
  IGetRunDetailProps,
  IGetWebhookRunsProps,
  IUpdateWebhookProps,
} from "./automation.types";
import type { ApiClient } from "./client";

export class WebhookApi {
  constructor(private apiClient: ApiClient) {}

  async getWebhooks(): Promise<IWebhook[]> {
    const response = await this.apiClient.get<IWebhook[]>(WEBHOOKS_URL);
    return response.data;
  }

  async getWebhook(uuid: string): Promise<IWebhook> {
    const response = await this.apiClient.get<IWebhook>(webhookUrl(uuid));
    return response.data;
  }

  async createWebhook(data: ICreateWebhookPayload): Promise<IWebhook> {
    const response = await this.apiClient.post<IWebhook>(WEBHOOKS_URL, data);
    return response.data;
  }

  async updateWebhook({ uuid, data }: IUpdateWebhookProps): Promise<IWebhook> {
    const response = await this.apiClient.put<IWebhook>(webhookUrl(uuid), data);
    return response.data;
  }

  async deleteWebhook(uuid: string): Promise<void> {
    await this.apiClient.delete(webhookUrl(uuid));
  }

  async toggleWebhook(uuid: string): Promise<IWebhook> {
    const response = await this.apiClient.put<IWebhook>(webhookToggleUrl(uuid));
    return response.data;
  }

  async bulkToggleWebhooks(
    payload: TBulkToggleWebhooksPayload,
  ): Promise<IBulkOperationResult> {
    const response = await this.apiClient.put<IBulkOperationResult>(
      WEBHOOKS_BULK_TOGGLE_URL,
      payload,
    );
    return response.data;
  }

  async bulkDeleteWebhooks(
    payload: TBulkDeleteWebhooksPayload,
  ): Promise<IBulkOperationResult> {
    const response = await this.apiClient.delete<IBulkOperationResult>(
      WEBHOOKS_BULK_DELETE_URL,
      { data: payload },
    );
    return response.data;
  }

  async regenerateWebhookToken(uuid: string): Promise<IWebhook> {
    const response = await this.apiClient.put<IWebhook>(
      webhookRegenerateTokenUrl(uuid),
    );
    return response.data;
  }

  async getWebhookRuns({
    uuid,
    page,
    limit,
  }: IGetWebhookRunsProps): Promise<IWebhookRunsListResponse> {
    const response = await this.apiClient.get<IWebhookRunsListResponse>(
      webhookRunsUrl({ uuid, page, limit }),
    );
    return response.data;
  }

  // NOTE: NSAI-5266 — per-run detail (the run + its ordered step trace).
  async getWebhookRunDetail({
    uuid,
    runUuid,
  }: IGetRunDetailProps): Promise<IAgentRunDetail<IWebhookRun>> {
    const response = await this.apiClient.get<IAgentRunDetail<IWebhookRun>>(
      webhookRunDetailUrl({ uuid, runUuid }),
    );
    return response.data;
  }
}
